/**
 * TagsKanbanStandalone - rota própria /funis (com NavSidebar).
 *
 * Existe porque "Funis" é CORE, não do canal: o usuário final não consegue
 * dizer em qual inbox está quando olha o Kanban. Antes desta rota,
 * `TagsKanbanPage` só era montada dentro dos shells de wa-personal e
 * wa-business (`case 'tags-kanban'`), e o item do trilho caía no
 * `onPageChange` do shell, que em Instagram e Messenger fazia
 * `navigate('/inbox' | '/wa-business', { state: { targetPage } })`: o Kanban
 * abria, mas dentro do shell do WhatsApp, com a lista de conversas do outro
 * canal ao lado. É a mesma classe do bug "Agentes de IA ejetava do canal",
 * fechado com a rota /agents (D6 do sweep 2B).
 *
 * Mesmo padrão de AgentsStandalone / FollowUpsStandalone / CalendarStandalone.
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import NavSidebar from '../../components/NavSidebar';
// Frente C1: /funis e filha do InboxLayout; com persistent_layout a
// NavSidebar mora la e sobrevive a ida e volta do inbox.
import { useInboxLayout } from '../../components/inbox/InboxLayout';
import TagsKanbanPage from './TagsKanbanPage';
import { channelOf, isOpaqueChannel, isOpaqueKey, openChatState, routeOf } from '../../utils/channels';

const TagsKanbanStandalone = () => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { user } = useAuth();
  const { layoutOwnsNav, setNavProps } = useInboxLayout();
  const isBusinessChannel = user?.default_channel === 'wa_business';

  // Mesmo padrão das outras páginas standalone: rota própria navega direto; o
  // resto cai no shell do inbox via targetPage.
  const handlePageChange = (page) => {
    if (page === 'tags-kanban') return;
    const standaloneRoutes = {
      'meta-leads': '/meta-leads',
      'saved-messages': '/saved-messages',
      'campaigns': '/campaigns',
      'followups': '/followups',
      'calendar': '/calendar',
      'empresa': '/empresa',
    };
    if (standaloneRoutes[page]) {
      navigate(standaloneRoutes[page]);
      return;
    }
    navigate(isBusinessChannel ? '/wa-business' : '/inbox', {
      state: { targetPage: page },
    });
  };

  // "Abrir conversa" no cartão: o destino sai do CANAL do cartão, nunca do
  // canal padrão da conta. Mesma resolução do `handleEnterForeign` da própria
  // TagsKanbanPage, com a chave opaca intacta (contato de Instagram e de
  // Messenger não tem telefone, e o backend manda `phone: null` neles). Os 4
  // shells leem `state.openChat` e casam por chat_id ou pela chave do canal.
  const handleOpenChat = (contact) => {
    const ref = String(contact?.contact_ref || contact?.phone || '');
    // Canal opaco (Instagram, Messenger, widget): o mapa de canais resolve a
    // rota e o campo do openChat pelo prefixo da chave (2026-09-23).
    if (isOpaqueKey(ref) || isOpaqueChannel(contact?.channel)) {
      const canal = isOpaqueKey(ref) ? channelOf(ref) : contact.channel;
      navigate(routeOf(canal), { state: { openChat: openChatState(canal, ref) } });
      return;
    }
    const rota = contact?.channel === 'wa_business' || isBusinessChannel
      ? '/wa-business'
      : '/inbox';
    navigate(rota, {
      state: { openChat: { contact_phone: contact?.phone || ref } },
    });
  };

  // Publica as props para a NavSidebar do layout (sem deps de proposito; o
  // layout so re-renderiza quando as props primitivas mudam).
  useEffect(() => {
    if (layoutOwnsNav) setNavProps({ currentPage: 'tags-kanban', onPageChange: handlePageChange });
  });

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      width: '100vw',
      // Frente C1: dentro do InboxLayout e uma coluna flex, nao a tela inteira.
      ...(layoutOwnsNav ? { width: 'auto', flex: 1, minWidth: 0 } : {}),
      overflow: 'hidden',
      backgroundColor: colors.bgSecondary,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    }}>
      {!layoutOwnsNav && (
        <NavSidebar currentPage="tags-kanban" onPageChange={handlePageChange} />
      )}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <TagsKanbanPage onOpenChat={handleOpenChat} />
      </div>
    </div>
  );
};

export default TagsKanbanStandalone;
